import { StageDef } from '../automationMachineInterfaces'
import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import * as selectAudienceUtils from './fbSelectAudienceModalUtils'
import {
  getSettingRowWithToggle,
  getSettingModalSelectedPrivacyLevel,
  getSettingToggleValue,
  openClickableSettingModal,
  closeSettingModal,
  setSettingToggleValue,
  setSettingModalPrivacyLevel,
} from './fbSettingsUtils'
import { profileAndTaggingSettingsUrl } from './fbUrls'
import { FacebookSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { FBRecommendationKey } from '../../utils/enums'
import * as enums from '../../utils/enums'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCantUndoFnFromKey,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { shouldUpdateSetting } from '../utils'

// Setting property functions
const currentValueFns = {
  [FacebookSettingKey.WHO_CAN_SEE_POSTS]: getCurrentValueFnFromKey(
    FacebookSettingKey.WHO_CAN_SEE_POSTS,
    (value) => (value ? value.name : null),
  ),
  [FacebookSettingKey.POST_SHARING]: getCurrentValueFnFromKey(
    FacebookSettingKey.POST_SHARING,
    (value) => (value ? 'On' : 'Off'),
  ),
}

const isFulfilledFns = {
  [FacebookSettingKey.WHO_CAN_SEE_POSTS]: getIsFulfilledFnFromKey(
    FacebookSettingKey.WHO_CAN_SEE_POSTS,
    (value) =>
      selectAudienceUtils.isPrivacySettingAsStrictOrStricter(
        value,
        selectAudienceUtils.FRIENDS,
      ),
  ),
  [FacebookSettingKey.POST_SHARING]: getIsFulfilledFnFromKey(
    FacebookSettingKey.POST_SHARING,
    (value) => !value,
  ),
}

const cantUndoFns = {
  [FacebookSettingKey.WHO_CAN_SEE_POSTS]: getCantUndoFnFromKey(
    FacebookSettingKey.WHO_CAN_SEE_POSTS,
    (value) =>
      value ? !selectAudienceUtils.isPrivacySettingUndoable(value) : false,
  ),
  [FacebookSettingKey.POST_SHARING]: defaultCantUndoFn,
}

const getCurrentValueFn = (key) => currentValueFns[key]
const getIsFulfilledFn = (key) => isFulfilledFns[key]
const getCantUndoFn = (key) => cantUndoFns[key]

const getIsDisabledFn = () => defaultIsDisabledFn
const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

const VIEWING_LABEL = 'Who can see what others post on your profile?'
// "stor" is hacky but intentional. some users have "story" and others "stories"
const SHARING_LABEL = 'Allow others to share your posts to their stor'

// Gather stage

enum StageKey {
  GATHER_SETTING_VIEWING_SHARING = 'GATHER_SETTING_VIEWING_SHARING',
  UPDATE_SETTING_VIEWING_SHARING = 'UPDATE_SETTING_VIEWING_SHARING',
  UNDO_SETTING_VIEWING_SHARING = 'UNDO_SETTING_VIEWING_SHARING',
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_VIEWING_SHARING,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: profileAndTaggingSettingsUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredInitialValues } = userContext

      await openClickableSettingModal([VIEWING_LABEL])
      const whoCanSeePostsValue =
        await getSettingModalSelectedPrivacyLevel(VIEWING_LABEL)
      await closeSettingModal(VIEWING_LABEL, 'CANCEL', {
        removeModalManually: true,
      })

      const sharePostsToStoriesRow =
        await getSettingRowWithToggle(SHARING_LABEL)
      const sharePostsValue = await getSettingToggleValue(
        sharePostsToStoriesRow,
      )

      updateStoredInitialValues(
        {
          [FacebookSettingKey.WHO_CAN_SEE_POSTS]: whoCanSeePostsValue,
          [FacebookSettingKey.POST_SHARING]: sharePostsValue,
        },
        FBRecommendationKey.FB_VIEWING_SHARING,
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_VIEWING_SHARING,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: profileAndTaggingSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (!settingsSelected) {
        return null
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.WHO_CAN_SEE_POSTS,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([VIEWING_LABEL])
        await setSettingModalPrivacyLevel(
          VIEWING_LABEL,
          isDry,
          selectAudienceUtils.FRIENDS,
        )
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.POST_SHARING,
          getIsFulfilledFn,
        )
      ) {
        const sharePostsToStoriesRow =
          await getSettingRowWithToggle(SHARING_LABEL)
        const sharePostsOn = await getSettingToggleValue(sharePostsToStoriesRow)
        if (sharePostsOn && !isDry) {
          await setSettingToggleValue(sharePostsToStoriesRow, false)
        }
      }

      return true
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_VIEWING_SHARING,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: profileAndTaggingSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (!settingsSelected) {
        return null
      }

      const initialWhoCanSeePosts =
        initialValues[FacebookSettingKey.WHO_CAN_SEE_POSTS]
      const initialPostSharing = initialValues[FacebookSettingKey.POST_SHARING]

      if (
        settingsSelected[FacebookSettingKey.WHO_CAN_SEE_POSTS] &&
        initialWhoCanSeePosts
      ) {
        await openClickableSettingModal([VIEWING_LABEL])
        await setSettingModalPrivacyLevel(
          VIEWING_LABEL,
          isDry,
          initialWhoCanSeePosts,
        )
      }

      if (
        settingsSelected[FacebookSettingKey.POST_SHARING] &&
        initialPostSharing
      ) {
        const sharePostsToStoriesRow =
          await getSettingRowWithToggle(SHARING_LABEL)
        const sharePostsOn = await getSettingToggleValue(sharePostsToStoriesRow)
        if (!sharePostsOn && !isDry) {
          await setSettingToggleValue(sharePostsToStoriesRow, true)
        }
      }

      return true
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
